<script setup lang="ts">
import TheBottomSheet from '@/components/TheBottomSheet.vue'
import StarsBalancePill from '@/components/StarsBalancePill.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import ThePremium from '@/components/ThePremium.vue'
import TonDiscountBadge from '@/components/TonDiscountBadge.vue'
import { computed, ref } from 'vue'
import type { CurrencyAmount } from '@/types/live-ops'
import { formatUnshortenedNumber } from '@/helpers/utils'
import api from '@/services/api'
import webapp from '@/services/webapp'
import { useWalletConnect } from '@/composables/game/wallet-connect'
import { sleep } from '@/services/helper'
import { useTonConnectModal } from '@townsquarelabs/ui-vue'
import { isBaseMiniApp } from '@/services/base'
import { jettonMasters, useEcommerce } from '@/composables/game/ecommerce'
import { player } from '@/composables/game/player'
import { useToaster } from '@/composables/game/toasters'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import { env } from '@/env'

type PaymentInfo = {
	itemId: string
	itemName: string
	usdAmount: number
	starAmount?: number
	tonAmount?: number
	dropeeAmount?: number
	rewards: readonly CurrencyAmount[] | CurrencyAmount[]
	action: Record<string, unknown>
}

const props = defineProps<{
	show: boolean
	paymentInfo: PaymentInfo | Readonly<PaymentInfo>
	userEventId: string | undefined
}>()

const emit = defineEmits<{
	(e: 'hide'): void
	(e: 'purchaseDone'): void
}>()

const { doTonPurchaseTransaction, doJettonPurchaseTransaction, rawAddress } = useWalletConnect()
const { open: openTonConnectModal } = useTonConnectModal()
const { addHapticFeedback } = useHapticFeedback()
const { showConfirmModal, starsBalanceConfirmModalConfig, buyingItemId } = useEcommerce()
const { notify } = useToaster()
const playerStarsBalance = computed(() => player.value.ecommerce.starsBalance)

const buyingWithCurrency = ref('')
const isBuyingWithStars = computed(() => buyingWithCurrency.value === 'star')
const isBuyingWithTon = computed(() => buyingWithCurrency.value === 'ton')
const isBuyingWithDropee = computed(() => buyingWithCurrency.value === 'dropee')

async function handlePurchase(currency: 'star' | 'ton' | 'dropee' | 'usdt') {
  addHapticFeedback('light')
  if (!props.userEventId) return

  const isStars = currency === 'star'

  try {
    // For non-star, ensure wallet is connected
    if (!isStars && !rawAddress.value) {
      openTonConnectModal()
      return
    }

    // Show confirmation if user has enough star balance (matches standard purchase flow)
    if (isStars && props.paymentInfo.starAmount && playerStarsBalance.value >= props.paymentInfo.starAmount) {
      const isConfirmed = await showConfirmModal(props.paymentInfo.starAmount)
      if (!isConfirmed) {
        return
      }
      buyingItemId.value = props.paymentInfo.itemId
    }

    buyingWithCurrency.value = currency

    const response = await api.liveOpsGenerateInvoice({
      userEventId: props.userEventId,
      action: props.paymentInfo.action,
      currency: currency,
    })

    if (response.isPaid) {
      addHapticFeedback('medium')
      starsBalanceConfirmModalConfig.value = { amount: 0, accepted: false }
      notify({ type: 'success', title: 'Purchase successful!' })
      emit('purchaseDone')
      return
    }

    // Execute payment based on currency
    if (isStars) {
      const result = await webapp.openInvoice(response.invoiceUrl)
      if (result !== 'paid') {
        return
      }
    } else if (currency === 'ton') {
      await doTonPurchaseTransaction({
        tonAmount: response.amount,
        nonce: response.nonce,
      })
    } else if (currency === 'dropee' || currency === 'usdt') {
      await doJettonPurchaseTransaction({
        amount: response.amount,
        address: jettonMasters[env.app.isProd ? 'prod' : 'dev'][currency],
        nonce: response.nonce,
      })
    } else {
      throw new Error(`Invalid currency ${currency}`)
    }

    // Poll for completion
    const maxAttempts = isStars ? 15 : 60
    const pollInterval = isStars ? 1000 : 3000

    for (let i = 0; i < maxAttempts; i++) {
      await sleep(pollInterval)
      const { status } = await api.getPurchaseStatus({ nonce: response.nonce })
      if (status === 'completed') {
        addHapticFeedback('medium')
        starsBalanceConfirmModalConfig.value = { amount: 0, accepted: false }
        notify({ type: 'success', title: 'Purchase successful!' })
        emit('purchaseDone')
        return
      }
    }
  } catch (e) {
    console.error('[LiveOpsPaymentModal] Payment failed:', e)
  } finally {
    buyingWithCurrency.value = ''
    buyingItemId.value = null
  }
}

function onClose() {
  emit('hide')
}
</script>

<template>
  <TheBottomSheet
    :show="show"
    bg-fade-class="!z-[802]"
    wrapper-class="!z-[802]"
    @hide="onClose"
  >
    <div class="w-full relative pt-6">
      <StarsBalancePill class="absolute left-0 -top-7" />

      <div class="mt-6 w-full flex flex-col items-center">
        <!-- <div
						v-if="itemImage"
						class="size-[120px] mx-auto mb-3"
					>
						<img
							:src="itemImage"
							class="w-full h-full object-contain"
							alt="Item"
						>
					</div> -->

        <div class="text-center text-light-white text-[24px] leading-8 font-bold -tracking-[0.48px]">
          {{ paymentInfo.itemName }} = ${{ paymentInfo.usdAmount.toFixed(2) }}
        </div>

        <div class="mt-8 w-full flex flex-col gap-y-3">
          <!-- Stars Button -->
          <BaseButton
            class="w-full"
            :loading="isBuyingWithStars"
            @click="handlePurchase('star')"
          >
            <span
              v-if="!isBuyingWithStars"
              class="flex items-center gap-x-2"
            >
              Buy with
              <ThePremium :with-tooltip="false" />
              <span
                v-if="paymentInfo.starAmount"
                class="-ml-0.5"
              >{{ formatUnshortenedNumber(paymentInfo.starAmount)
              }}</span>
            </span>
            <span v-else>Processing...</span>
          </BaseButton>

          <!-- TON Button (only outside Base miniapp) -->
          <div
            v-if="!isBaseMiniApp"
            class="relative w-full"
          >
            <div class="absolute right-3 -top-1 rotate-[20deg] z-[1]">
              <TonDiscountBadge label="-10%" />
            </div>
            <BaseButton
              style-type="secondary"
              class="w-full"
              :loading="isBuyingWithTon"
              @click="handlePurchase('ton')"
            >
              <span
                v-if="!isBuyingWithTon"
                class="flex items-center gap-x-2"
              >
                Buy with
                <img
                  src="@/assets/images/ton-symbol.png"
                  class="size-5 min-w-[20px] object-cover"
                  alt="TON symbol"
                >
                <span v-if="paymentInfo.tonAmount">{{ paymentInfo.tonAmount }}</span>
              </span>
              <span v-else>Processing...</span>
            </BaseButton>
          </div>

          <!-- $DROPEE Button (only outside Base miniapp) -->
          <div
            v-if="!env.app.isProd && !isBaseMiniApp"
            class="relative w-full"
          >
            <div class="absolute right-3 -top-1 rotate-[20deg] z-[1]">
              <TonDiscountBadge label="-20%" />
            </div>
            <BaseButton
              style-type="secondary"
              class="w-full"
              :loading="isBuyingWithDropee"
              @click="handlePurchase('dropee')"
            >
              <span
                v-if="!isBuyingWithDropee"
                class="flex items-center gap-x-2"
              >
                Buy with
                <img
                  src="@/assets/images/dropee-token-new.webp"
                  class="size-5 min-w-[20px] object-cover"
                  alt="$DROPEE symbol"
                >
                <span v-if="paymentInfo.tonAmount">{{ paymentInfo.tonAmount }}</span>
              </span>
              <span v-else>Processing...</span>
            </BaseButton>
          </div>

          <div
            v-if="!isBaseMiniApp"
            class="mx-auto -mt-1 max-w-[307px] text-center text-xs -tracking-[0.28px] text-white/[.87] font-medium"
          >
            Transaction may take a few minutes to process. You'll be notified once it's successful.
          </div>
        </div>
      </div>
    </div>
  </TheBottomSheet>
</template>
